import moment from "moment";
import { useTranslation } from "react-i18next";
import { addDays } from "../../../../utils/flexible-program";
import {
  buildWeekName,
  programWeekNumber,
} from "../../../../utils/import-template-selection";

const pretty = (yyyymmdd) =>
  moment(String(yyyymmdd), "YYYYMMDD").format("MMM D");
const range = (start) => `${pretty(start)} – ${pretty(addDays(start, 6))}`;

/**
 * "Where it lands": the program's weeks around the import, with the imported
 * weeks (named as they will be saved) next to what they replace.
 */
const ImportTimeline = ({
  landings,
  blocks,
  templateName,
  naming,
  existingWeeks,
  startDate,
  anchor,
  loggedStarts = [],
}) => {
  const { t } = useTranslation("coaching-portal");
  if (!landings.length) return null;

  const byDate = new Map(
    landings.map((landing) => [landing.startDate, landing])
  );
  const existingByDate = new Map(
    existingWeeks.map((week) => [Number(week.startDate), week])
  );
  const blockByOrder = new Map(blocks.map((block) => [block.order, block]));
  const last = landings[landings.length - 1].startDate;

  const rows = [];
  for (
    let date = addDays(startDate, -7);
    date <= last;
    date = addDays(date, 7)
  ) {
    if (date < anchor) continue;
    const landing = byDate.get(date);
    const existing = existingByDate.get(date);
    const existingCount = existing?.sessions?.length || 0;
    const block = landing && blockByOrder.get(landing.blockOrder);
    const programWeek = programWeekNumber(anchor, date);

    rows.push({
      date,
      landing,
      existing,
      existingCount,
      block,
      programWeek,
      name: landing
        ? buildWeekName({
            naming,
            templateName,
            blockName: block?.name,
            blockOrder: landing.blockOrder,
            week: landing.week,
            programWeek: landing.programWeek,
            startDate: date,
            order: landing.programWeek,
          })
        : existing?.name,
    });
  }

  return (
    <div>
      <div className="imp-sec-head sm" style={{ marginBottom: "0.5rem" }}>
        <h3>{t("flexible.importTemplate.timeline")}</h3>
        <span className="imp-count">
          {t("flexible.importTemplate.timelineRange", {
            from: landings[0].programWeek,
            to: landings[landings.length - 1].programWeek,
          })}
        </span>
      </div>
      <div className="imp-timeline">
        {rows.map((row) => (
          <div
            key={row.date}
            className={`imp-tl-row${row.date < startDate ? " before" : ""}`}
          >
            <span className="imp-tl-wk">
              {t("flexible.importTemplate.weekShort", {
                week: row.programWeek,
              })}
            </span>
            <div className="imp-tl-mid">
              <div className="imp-tl-name">{row.name}</div>
              <div className="imp-tl-dates">
                {range(row.date)}
                {row.block &&
                  ` · ${
                    row.block.name ||
                    t("flexible.importTemplate.blockFallback", {
                      order: row.block.order,
                    })
                  }`}
                {!row.landing &&
                  ` · ${t("flexible.importTemplate.alreadyInProgram")}`}
              </div>
            </div>
            <span className="imp-tags">
              {loggedStarts.includes(row.date) && (
                <span className="imp-pill warning">
                  {t("flexible.importTemplate.loggedWork")}
                </span>
              )}
              {row.landing && (
                <span className="imp-pill info">
                  {t("flexible.importTemplate.workoutsCount", {
                    count: row.landing.workouts,
                  })}
                </span>
              )}
              {row.landing && row.existingCount > 0 && (
                <span className="imp-pill orange">
                  {t("flexible.importTemplate.replacesCount", {
                    count: row.existingCount,
                  })}
                </span>
              )}
              {!row.landing && row.existingCount > 0 && (
                <span className="imp-pill">
                  {t("flexible.importTemplate.existingCount", {
                    count: row.existingCount,
                  })}
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ImportTimeline;
